import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { taLocalsApi } from '../../services/api';
import { 
  TALocalDto, 
  CreateTALocalRequest, 
  TALocalStatsDto,
  TALocalDescription,
  PRODUCT_CATEGORIES,
  NZ_REGIONS,
  APPROVAL_PENDING,
  APPROVAL_APPROVED,
  APPROVAL_REJECTED,
  ApprovalChargePreview
} from '../../types/TALocal';
import ListingImage from './ListingImage';

const messageFrom = (err: unknown, fallback: string): string => {
  const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
  return data?.message || fallback;
};

const statusBadge = (local: TALocalDto): { label: string; className: string } => {
  if (local.deleted) return { label: 'Deleted', className: 'bg-red-100 text-red-800' };
  if (local.approvalStatus === APPROVAL_REJECTED) return { label: 'Rejected', className: 'bg-gray-200 text-gray-800' };
  if (local.approvalStatus !== APPROVAL_APPROVED) return { label: 'Pending approval', className: 'bg-amber-100 text-amber-900' };
  if (local.poiStatus === 1) return { label: 'Active', className: 'bg-green-100 text-green-800' };
  return { label: 'Inactive', className: 'bg-yellow-100 text-yellow-800' };
};

/**
 * The photos, offer, website and contact details a listing carries live in a
 * JSON blob on `description`. Everything the review modal shows beyond name and
 * co-ordinates comes from here, so a listing that skips this step renders as an
 * empty shell - which is how the CiviCRM deep link first arrived, showing the
 * reviewer less than the View button did.
 *
 * A listing whose JSON will not parse still opens, without the extra detail,
 * rather than taking the page down with it.
 */
/**
 * Why a deep link would not open. Saying "it may have been deleted" for a
 * permissions failure sends someone looking for the wrong thing, and on the
 * volunteer page it contradicts the access panel rendered beside it.
 */
const deepLinkMessage = (err: unknown, id: string, noun: string): string => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 404) return `That ${noun} (${id}) no longer exists. It may have been deleted.`;
  if (status === 403) return `You do not have permission to open ${noun} ${id}.`;
  return `Could not open ${noun} ${id}. The server did not respond as expected.`;
};

const withParsedDescription = (local: TALocalDto): TALocalDto => {
  if (!local.description) return local;
  try {
    return { ...local, parsedDescription: JSON.parse(local.description) as TALocalDescription };
  } catch {
    console.error(`TA Local ${local.id} has a description that is not valid JSON`);
    return local;
  }
};

/** Cents to "$150.00", in whatever currency Stripe quoted. */
function money(cents: number, currency: string): string {
  // Intl throws RangeError on a currency code it does not know, and there is no
  // error boundary around the panel - a bad code would take the whole modal
  // down rather than one line of it.
  try {
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/**
 * What the success alert says. "The partner's card has been charged" is wrong
 * for a wholly discounted listing, and wrong in the same way the old confirm
 * box was - the preview is right there to make it accurate.
 */
function approvedMessage(local: TALocalDto, preview: ApprovalChargePreview | null): string {
  if (preview?.chargeNowCents == null) {
    return `"${local.name}" is approved and the listing is live.`;
  }
  const charged = preview.chargeNowCents === 0
    ? 'Nothing was charged'
    : `The partner's card has been charged ${money(preview.chargeNowCents, preview.currency)}`;
  return `"${local.name}" is approved. ${charged} and the listing is live.`;
}

/**
 * What the confirm box says. It quotes the figure from the preview rather than
 * "the annual listing fee", because the approver is agreeing to a charge and
 * the two are not the same number once a code is involved.
 */
function approvalPrompt(local: TALocalDto, preview: ApprovalChargePreview | null): string {
  // The refusal first: this is the box someone clicks OK on, and the panel
  // saying "approving would be refused" is no use if the dialog does not.
  const warning = preview?.refusal ? `\n\nThe charge panel says this would be refused: ${preview.refusal}` : '';

  if (preview?.chargeNowCents == null) {
    // Deliberately not "charged the annual listing fee": this is the case where
    // the fee could not be read, and the panel above says so. Asserting it here
    // is the sentence this whole change exists to stop.
    return `Approve "${local.name}"? What will be charged could not be worked out`
      + ` — check the charge panel before continuing. The listing goes live if it succeeds.${warning}`;
  }
  const now = money(preview.chargeNowCents, preview.currency);
  const charge = preview.chargeNowCents === 0
    ? 'Nothing is charged'
    : `The partner's card is charged ${now}`;
  const renewal = preview.chargeOnRenewalCents != null && preview.chargeOnRenewalCents !== preview.chargeNowCents
    ? ` Next year it renews at ${money(preview.chargeOnRenewalCents, preview.currency)}.`
    : '';
  return `Approve "${local.name}"? ${charge} now and the listing goes live.${renewal}${warning}`;
}

/**
 * The fee, the code, what the code is worth, and the net — in front of the
 * person about to agree to it. Every figure comes from the API; a missing one
 * is shown as unknown rather than filled in, because this panel is read as the
 * charge.
 */
function ChargePanel({ preview, failed }: { preview: ApprovalChargePreview | null; failed: boolean }) {
  if (failed) {
    return (
      <p className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        Could not work out what approving this will charge. Check Stripe before approving.
      </p>
    );
  }
  if (!preview) {
    return <p className="mb-3 text-sm text-gray-500">Working out what this will charge…</p>;
  }

  const unknown = <span className="text-gray-500">unknown</span>;
  const amount = (cents: number | null) =>
    cents == null ? unknown : money(cents, preview.currency);

  // Indexed with a fallback on purpose. A Record<string, string> lookup is an
  // unguarded any here, and when the status arrived as a number rather than a
  // name this silently rendered empty - the one line the panel exists to show.
  const codeLabel: Record<string, string> = {
    None: 'No code on this listing',
    Active: 'Stripe will honour this code',
    NotRecognised: 'Stripe does not recognise this code',
    AlreadyApplied: 'Already recorded as applied; Stripe will apply it again',
    CouldNotCheck: 'Could not be checked with Stripe',
  };
  const codeExplanation = codeLabel[preview.codeStatus] ?? `Status: ${preview.codeStatus}`;

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <h4 className="text-sm font-semibold text-gray-900">What approving charges</h4>
      <dl className="mt-2 space-y-1 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-gray-600">Listing fee</dt>
          <dd className="text-gray-900">{amount(preview.listPriceCents)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-gray-600">Promo code</dt>
          <dd className="text-right text-gray-900">
            {preview.promoCode ? <span className="font-mono">{preview.promoCode}</span> : '—'}
            <span className="block text-xs text-gray-500">{codeExplanation}</span>
          </dd>
        </div>
        {preview.discountSummary && (
          <div className="flex justify-between gap-4">
            <dt className="text-gray-600">Discount</dt>
            <dd className="text-right text-gray-900">{preview.discountSummary}</dd>
          </div>
        )}
        <div className="flex justify-between gap-4 border-t border-gray-200 pt-1 font-semibold">
          <dt className="text-gray-900">Charged now</dt>
          <dd className="text-gray-900">{amount(preview.chargeNowCents)}</dd>
        </div>
        {preview.autoRenew && (
          <div className="flex justify-between gap-4">
            <dt className="text-gray-600">Renews next year at</dt>
            <dd className="text-gray-900">{amount(preview.chargeOnRenewalCents)}</dd>
          </div>
        )}
        {!preview.autoRenew && (
          <div className="flex justify-between gap-4">
            <dt className="text-gray-600">Renewal</dt>
            <dd className="text-gray-900">The partner asked not to renew</dd>
          </div>
        )}
        <div className="flex justify-between gap-4">
          <dt className="text-gray-600">Card on file</dt>
          <dd className="text-gray-900">
            {preview.cardOnFile == null ? unknown : preview.cardOnFile ? 'Yes' : 'No'}
          </dd>
        </div>
      </dl>

      {preview.caveat && (
        <p className="mt-2 text-xs text-amber-800">{preview.caveat}</p>
      )}
      {preview.refusal && (
        <p role="alert" className="mt-2 text-sm font-medium text-red-700">
          Approving would be refused: {preview.refusal}
        </p>
      )}
    </div>
  );
}

const TALocalsPage: React.FC = () => {
  const [locals, setLocals] = useState<TALocalDto[]>([]);
  const [stats, setStats] = useState<TALocalStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selectedLocals, setSelectedLocals] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLocal, setEditingLocal] = useState<TALocalDto | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  // What approving will charge. Fetched when the modal opens, because this is
  // the moment someone decides, and the figures come from Stripe rather than
  // from anything the listing itself carries.
  const [chargePreview, setChargePreview] = useState<ApprovalChargePreview | null>(null);
  const [previewError, setPreviewError] = useState(false);
  // Bumped on every open. Keyed on the id alone, closing and reopening the same
  // listing left the deps unchanged and the panel showed the previous answer -
  // wrong in the one flow that matters: approve fails for want of a card, the
  // partner adds one, the admin reopens, and the stale panel still says No.
  const [previewNonce, setPreviewNonce] = useState(0);
  const [viewingLocal, setViewingLocal] = useState<TALocalDto | null>(null);
  const [approvalMessages, setApprovalMessages] = useState<Record<string, string>>({});
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [deepLinkError, setDeepLinkError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Form state for create/edit
  const [formData, setFormData] = useState<CreateTALocalRequest>({
    name: '',
    latitude: 0,
    longitude: 0,
    region: '',
    introductionThumbnail: '',
    streetAddress: '',
    phoneNumber: '',
    email: '',
    website: '',
    productCategory: '',
    offer: '',
    logoUrl: '',
    imageUrls: [],
    poiStatus: 1
  });

  const loadLocals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await taLocalsApi.getLocals({
        page,
        pageSize,
        search: searchTerm || undefined,
        region: selectedRegion || undefined,
        category: selectedCategory || undefined,
        includeDeleted
      });
      
      setLocals(response.locals.map(withParsedDescription));
      setTotalPages(response.totalPages);
    } catch (err) {
      console.error('Failed to load TA Locals', err);
      alert('Failed to load TA Locals');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, selectedRegion, selectedCategory, includeDeleted]);

  const loadStats = async () => {
    try {
      const statsData = await taLocalsApi.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  };

  useEffect(() => {
    loadLocals();
    loadStats();
  }, [loadLocals]);

  const handleSearch = () => {
    setPage(1);
    loadLocals();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRegion('');
    setSelectedCategory('');
    setIncludeDeleted(false);
    setPage(1);
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      latitude: 0,
      longitude: 0,
      region: '',
      introductionThumbnail: '',
      streetAddress: '',
      phoneNumber: '',
      email: '',
      website: '',
      productCategory: '',
      offer: '',
      logoUrl: '',
      imageUrls: [],
      poiStatus: 1
    });
  };

  const handleCreate = () => {
    resetFormData();
    setEditingLocal(null);
    setShowCreateModal(true);
  };

  const handleEdit = (local: TALocalDto) => {
    const desc = local.parsedDescription;
    setFormData({
      name: local.name,
      latitude: local.latitude,
      longitude: local.longitude,
      region: local.region,
      introductionThumbnail: local.introductionThumbnail,
      streetAddress: desc?.["Street Address"] || '',
      phoneNumber: desc?.["Phone Number"] || '',
      email: desc?.["Email"] || '',
      website: desc?.["Website"] || '',
      productCategory: desc?.["Product Category"] || '',
      offer: desc?.["Offer"] || '',
      logoUrl: desc?.["logoUrl"] || local.introductionThumbnail,
      imageUrls: desc?.["imageUrls"] || [],
      poiStatus: local.poiStatus
    });
    setEditingLocal(local);
    setShowCreateModal(true);
  };

  // CiviCRM's "TA Local listings awaiting approval" search links each listing
  // straight here as ?listing=<id>, where the id is the POI's PkId - the same
  // value TALocalDto.id carries. The listing is fetched by id rather than
  // looked for in the loaded page, because the one being reviewed is usually
  // not on the page the current filters happen to show.
  useEffect(() => {
    const id = searchParams.get('listing');
    if (!id) return;
    // The backend binds this as an int; anything else is a malformed link, not
    // a request worth making.
    if (!/^\d+$/.test(id)) {
      setDeepLinkError(`"${id}" is not a listing id.`);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const local = await taLocalsApi.getLocal(id);
        if (cancelled) return;
        setViewingLocal(withParsedDescription(local));
        setShowViewModal(true);
        setPreviewNonce((n) => n + 1);
        // Consumed: a refresh after closing should not reopen it.
        setSearchParams({}, { replace: true });
      } catch (err) {
        if (cancelled) return;
        setDeepLinkError(deepLinkMessage(err, id, 'listing'));
        console.error(err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Runs once: the link is how you arrived, not something that changes after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleView = (local: TALocalDto) => {
    setViewingLocal(local);
    setShowViewModal(true);
    setPreviewNonce((n) => n + 1);
  };

  // Gated on the same condition as the panel: three Stripe calls for a listing
  // whose panel never renders is three calls for nothing. Keyed on the id, not
  // the object, so a re-render with an equal listing does not refetch.
  // The id plus the open counter, so reopening the same listing refetches while
  // a re-render with an equal listing does not.
  const previewKey = showViewModal && viewingLocal && !viewingLocal.deleted
    && viewingLocal.approvalStatus !== APPROVAL_APPROVED
    ? `${viewingLocal.id}#${previewNonce}`
    : null;

  useEffect(() => {
    if (previewKey == null) return;
    const id = previewKey.slice(0, previewKey.lastIndexOf('#'));
    let cancelled = false;
    setChargePreview(null);
    setPreviewError(false);
    taLocalsApi
      .getChargePreview(id)
      .then((p) => { if (!cancelled) setChargePreview(p); })
      .catch(() => { if (!cancelled) setPreviewError(true); });
    return () => { cancelled = true; };
  }, [previewKey]);

  const handleSave = async () => {
    try {
      if (editingLocal) {
        await taLocalsApi.updateLocal(editingLocal.id, formData);
        alert('TA Local updated successfully');
      } else {
        await taLocalsApi.createLocal(formData);
        alert('TA Local created successfully');
      }
      setShowCreateModal(false);
      resetFormData();
      setEditingLocal(null);
      await loadLocals();
      await loadStats();
    } catch (err) {
      alert('Failed to save TA Local');
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this TA Local?')) return;
    
    try {
      await taLocalsApi.deleteLocal(id);
      await loadLocals();
      await loadStats();
    } catch (err) {
      alert('Failed to delete TA Local');
      console.error(err);
    }
  };

  const handleApprove = async (local: TALocalDto) => {
    if (!window.confirm(approvalPrompt(local, chargePreview))) return;

    setApprovingId(local.id);
    setApprovalMessages((m) => ({ ...m, [local.id]: '' }));
    try {
      await taLocalsApi.approveLocal(local.id);
      // Only once the charge has actually gone through. Closing first left a
      // declined card with nowhere to report it: the modal was gone, and the
      // list card that also shows the error is usually not on screen, because
      // a listing opened from CiviCRM is rarely on the page the filters loaded.
      setShowViewModal(false);
      await loadLocals();
      await loadStats();
      window.alert(approvedMessage(local, chargePreview));
    } catch (err) {
      setApprovalMessages((m) => ({ ...m, [local.id]: messageFrom(err, 'Failed to approve TA Local') }));
      console.error(err);
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (local: TALocalDto) => {
    const reason = window.prompt(`Reject "${local.name}"? Nothing is charged.\n\nReason (the partner sees this in their email and portal):`);
    if (reason === null) return;

    // approvingId is what disables both buttons; without it the Reject button's
    // own disabled check never engaged, which only went unnoticed while the
    // modal closed instantly.
    setApprovingId(local.id);
    setApprovalMessages((m) => ({ ...m, [local.id]: '' }));
    try {
      await taLocalsApi.rejectLocal(local.id, reason.trim().slice(0, 500));
      setShowViewModal(false);
      await loadLocals();
      await loadStats();
      window.alert(`"${local.name}" is rejected. Nothing was charged, and the partner has been emailed your reason.`);
    } catch (err) {
      setApprovalMessages((m) => ({ ...m, [local.id]: messageFrom(err, 'Failed to reject TA Local') }));
      console.error(err);
    } finally {
      setApprovingId(null);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await taLocalsApi.restoreLocal(id);
      await loadLocals();
      await loadStats();
    } catch (err) {
      alert('Failed to restore TA Local');
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLocals.size === 0) {
      alert('No locals selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedLocals.size} TA Locals?`)) return;

    try {
      const localIds = Array.from(selectedLocals);
      for (const localId of localIds) {
        await taLocalsApi.deleteLocal(localId);
      }
      setSelectedLocals(new Set());
      await loadLocals();
      await loadStats();
    } catch (err) {
      alert('Failed to delete TA Locals');
      console.error(err);
    }
  };

  const toggleLocalSelection = (localId: string) => {
    const newSelection = new Set(selectedLocals);
    if (newSelection.has(localId)) {
      newSelection.delete(localId);
    } else {
      newSelection.add(localId);
    }
    setSelectedLocals(newSelection);
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Retail': 'bg-blue-100 text-blue-800',
      'Food & Beverage': 'bg-green-100 text-green-800',
      'Accommodation': 'bg-purple-100 text-purple-800',
      'Transport': 'bg-yellow-100 text-yellow-800',
      'Health & Wellbeing': 'bg-pink-100 text-pink-800',
      'Leisure': 'bg-indigo-100 text-indigo-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading && locals.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white shadow-sm rounded-xl border border-gray-200">
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Loading TA Locals...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="text-indigo-600 hover:text-indigo-900">
            ← Back to Dashboard
          </Link>
          {/* A link from CiviCRM that led nowhere used to look like the page
              simply loading normally, which is worse than saying so. */}
          {deepLinkError && (
            <p role="alert" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {deepLinkError}
            </p>
          )}
          <div className="flex justify-between items-center mt-4">
            <h1 className="text-3xl font-bold text-gray-900">TA Locals Management</h1>
            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add New Local
            </button>
          </div>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-gray-900">{stats.totalLocals}</div>
              <div className="text-sm text-gray-500">Total Locals</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{stats.activeLocals}</div>
              <div className="text-sm text-gray-500">Active Locals</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-red-600">{stats.deletedLocals}</div>
              <div className="text-sm text-gray-500">Deleted Locals</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters & Search</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear All
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <svg className="absolute left-3 top-3 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 px-3 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Regions</option>
              {NZ_REGIONS.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Categories</option>
              {PRODUCT_CATEGORIES.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm">Include Deleted</span>
              </label>
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedLocals.size > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-xl mb-6">
            <div className="flex items-center justify-between">
              <span className="font-medium text-indigo-900">
                {selectedLocals.size} locals selected
              </span>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Delete Selected
              </button>
            </div>
          </div>
        )}

        {/* Locals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locals.map((local) => {
            const desc = local.parsedDescription;
            return (
              <div
                key={local.id}
                className={`bg-white rounded-xl border transition-all duration-200 hover:shadow-lg flex flex-col h-full ${
                  local.deleted 
                    ? 'border-red-200 bg-red-50' 
                    : selectedLocals.has(local.id)
                    ? 'border-indigo-300 shadow-sm'
                    : 'border-gray-200'
                }`}
              >
                {/* Card Image Header */}
                <div className="relative h-48 bg-gray-100 rounded-t-xl overflow-hidden">
                  <ListingImage
                    desc={desc}
                    thumbnail={local.introductionThumbnail}
                    alt={local.name}
                    colorClass={getCategoryColor(desc?.["Product Category"] ?? '')}
                    className="w-full h-full"
                  />
                  {/* Checkbox overlay */}
                  <div className="absolute top-3 left-3">
                    <input
                      type="checkbox"
                      checked={selectedLocals.has(local.id)}
                      onChange={() => toggleLocalSelection(local.id)}
                      className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded bg-white shadow-sm"
                    />
                  </div>
                  {/* Status badge overlay */}
                  <div className="absolute top-3 right-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusBadge(local).className}`}>
                      {statusBadge(local).label}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="flex flex-col flex-1 p-5">
                  {/* Business Name */}
                  <h3 className="font-semibold text-lg text-gray-900 mb-3">{local.name}</h3>
                  
                  {/* Business Details */}
                  {desc && (
                    <div className="flex-1 mb-4">
                      {/* Category and location */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(desc["Product Category"])}`}>
                          {desc["Product Category"]}
                        </span>
                        <span className="text-xs text-gray-500">{local.region}</span>
                        {desc["imageUrls"] && desc["imageUrls"].length > 0 && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {desc["imageUrls"].length}
                          </span>
                        )}
                      </div>
                      
                      {/* Special offer */}
                      {desc["Offer"] && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {desc["Offer"]}
                        </p>
                      )}
                      
                      {/* Contact info */}
                      <div className="text-xs text-gray-500 space-y-1">
                        {desc["Street Address"] && (
                          <div className="flex items-center gap-1">
                            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="truncate">{desc["Street Address"]}</span>
                          </div>
                        )}
                        {desc["Phone Number"] && (
                          <div className="flex items-center gap-1">
                            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>{desc["Phone Number"]}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Approving charges a real card, so the decision lives in the
                      review modal - the card can only send you there to look. */}
                  {!local.deleted && local.approvalStatus !== APPROVAL_APPROVED && (
                    <div className="mt-auto pt-2">
                      <button
                        onClick={() => handleView(local)}
                        className="w-full px-3 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Review listing
                      </button>
                      {approvalMessages[local.id] && (
                        <p role="alert" className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                          {approvalMessages[local.id]}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Actions - Always at bottom */}
                  <div className={`flex items-center gap-2 pt-2 ${local.deleted || local.approvalStatus === APPROVAL_APPROVED ? 'mt-auto' : ''}`}>
                    <button
                      onClick={() => handleView(local)}
                      className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      View
                    </button>
                    {!local.deleted && (
                      <>
                        <button
                          onClick={() => handleEdit(local)}
                          className="flex-1 px-3 py-2 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(local.id)}
                          className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                    {local.deleted && (
                      <button
                        onClick={() => handleRestore(local.id)}
                        className="flex-1 px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                      >
                        Restore
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <nav className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-6">
                  {editingLocal ? 'Edit TA Local' : 'Create New TA Local'}
                </h2>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Region *
                      </label>
                      <select
                        value={formData.region}
                        onChange={(e) => setFormData({...formData, region: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        required
                      >
                        <option value="">Select Region</option>
                        {NZ_REGIONS.map(region => (
                          <option key={region} value={region}>{region}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Latitude *
                      </label>
                      <input
                        type="number"
                        step="0.000001"
                        value={formData.latitude}
                        onChange={(e) => setFormData({...formData, latitude: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Longitude *
                      </label>
                      <input
                        type="number"
                        step="0.000001"
                        value={formData.longitude}
                        onChange={(e) => setFormData({...formData, longitude: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      value={formData.streetAddress}
                      onChange={(e) => setFormData({...formData, streetAddress: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="text"
                        value={formData.phoneNumber}
                        onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Website
                      </label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({...formData, website: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Category *
                      </label>
                      <select
                        value={formData.productCategory}
                        onChange={(e) => setFormData({...formData, productCategory: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        required
                      >
                        <option value="">Select Category</option>
                        {PRODUCT_CATEGORIES.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Special Offer for TA Walkers *
                    </label>
                    <textarea
                      value={formData.offer}
                      onChange={(e) => setFormData({...formData, offer: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      rows={3}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logo URL
                    </label>
                    <input
                      type="url"
                      value={formData.logoUrl}
                      onChange={(e) => setFormData({...formData, logoUrl: e.target.value, introductionThumbnail: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Images
                    </label>
                    <div className="space-y-2">
                      {(formData.imageUrls || []).map((url, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="url"
                            value={url}
                            onChange={(e) => {
                              const newUrls = [...(formData.imageUrls || [])];
                              newUrls[index] = e.target.value;
                              setFormData({...formData, imageUrls: newUrls});
                            }}
                            placeholder={`Image URL ${index + 1}`}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newUrls = (formData.imageUrls || []).filter((_, i) => i !== index);
                              setFormData({...formData, imageUrls: newUrls});
                            }}
                            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({...formData, imageUrls: [...(formData.imageUrls || []), '']});
                        }}
                        className="w-full px-3 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-indigo-500 hover:text-indigo-600"
                      >
                        + Add Image URL
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        POI Status
                      </label>
                      <select
                        value={formData.poiStatus}
                        onChange={(e) => setFormData({...formData, poiStatus: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value={1}>Active</option>
                        <option value={0}>Inactive</option>
                      </select>
                    </div>
                    
                    {editingLocal && editingLocal.approvalStatus !== APPROVAL_APPROVED && (
                      <p className="self-end text-xs text-gray-500 pb-2">
                        Approval is done from the review screen, and charges the partner.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetFormData();
                      setEditingLocal(null);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    {editingLocal ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && viewingLocal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-start justify-between mb-6">
                  <h2 className="text-2xl font-bold">{viewingLocal.name}</h2>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setViewingLocal(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <ListingImage
                  desc={viewingLocal.parsedDescription}
                  thumbnail={viewingLocal.introductionThumbnail}
                  alt={viewingLocal.name}
                  colorClass={getCategoryColor(viewingLocal.parsedDescription?.["Product Category"] ?? '')}
                  className="w-full h-48 rounded-lg mb-6"
                />

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Region</h3>
                      <p className="text-gray-900">{viewingLocal.region}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Category</h3>
                      <p className="text-gray-900">{viewingLocal.parsedDescription?.["Product Category"]}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Latitude</h3>
                      <p className="text-gray-900">{viewingLocal.latitude}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Longitude</h3>
                      <p className="text-gray-900">{viewingLocal.longitude}</p>
                    </div>
                  </div>

                  {viewingLocal.parsedDescription && (
                    <>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Address</h3>
                        <p className="text-gray-900">{viewingLocal.parsedDescription["Street Address"]}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Phone</h3>
                          <p className="text-gray-900">{viewingLocal.parsedDescription["Phone Number"]}</p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Email</h3>
                          <p className="text-gray-900">{viewingLocal.parsedDescription["Email"]}</p>
                        </div>
                      </div>

                      {viewingLocal.parsedDescription["Website"] && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Website</h3>
                          <a 
                            href={viewingLocal.parsedDescription["Website"]} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-800"
                          >
                            {viewingLocal.parsedDescription["Website"]}
                          </a>
                        </div>
                      )}

                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Special Offer</h3>
                        <p className="text-gray-900">{viewingLocal.parsedDescription["Offer"]}</p>
                      </div>

                      {viewingLocal.parsedDescription["imageUrls"] && viewingLocal.parsedDescription["imageUrls"].length > 0 && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-2">Additional Images</h3>
                          <div className="grid grid-cols-2 gap-2">
                            {viewingLocal.parsedDescription["imageUrls"].map((url: string, index: number) => (
                              <img 
                                key={index}
                                src={url} 
                                alt={`${viewingLocal.name} gallery ${index + 1}`}
                                className="w-full h-32 object-cover rounded-lg border border-gray-200"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23f3f4f6"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%239ca3af" font-family="system-ui" font-size="12"%3EImage not found%3C/text%3E%3C/svg%3E';
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Status</h3>
                      <p className="text-gray-900">{statusBadge(viewingLocal).label}</p>
                      {viewingLocal.approvalStatus === APPROVAL_REJECTED && viewingLocal.rejectionReason && (
                        <p className="text-sm text-gray-600 mt-1">Reason: {viewingLocal.rejectionReason}</p>
                      )}
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Created</h3>
                      <p className="text-gray-900">{new Date(viewingLocal.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {/* The decision is made here, having seen the listing above,
                      rather than from a card that shows none of it. */}
                  {!viewingLocal.deleted && viewingLocal.approvalStatus !== APPROVAL_APPROVED && (
                    <div className="border-t border-gray-200 pt-4">
                      {approvalMessages[viewingLocal.id] && (
                        <p role="alert" className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                          {approvalMessages[viewingLocal.id]}
                        </p>
                      )}
                      <ChargePanel preview={chargePreview} failed={previewError} />
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleApprove(viewingLocal)}
                          disabled={approvingId === viewingLocal.id}
                          className="flex-1 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          {approvingId === viewingLocal.id ? 'Charging…' : 'Approve & charge'}
                        </button>
                        {viewingLocal.approvalStatus === APPROVAL_PENDING && (
                          <button
                            onClick={() => handleReject(viewingLocal)}
                            disabled={approvingId !== null}
                            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
                          >
                            Reject
                          </button>
                        )}
                      </div>
                      {/* Reads from the panel rather than asserting a charge:
                          under a $0.00 / Card on file: No panel the old static
                          line contradicted everything above it. */}
                      <p className="mt-2 text-xs text-gray-500">
                        {chargePreview?.chargeNowCents === 0
                          ? 'Approving puts the listing on the trail map. Nothing is charged.'
                          : chargePreview?.chargeNowCents != null
                            ? `Approving charges ${money(chargePreview.chargeNowCents, chargePreview.currency)} `
                              + 'to the card on file and puts the listing on the trail map.'
                            : 'Approving charges the card on file and puts the listing on the trail map.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TALocalsPage;